"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { cardnexusConnectUrl, cardnexusFetch } from "@/lib/cardnexus/browser";
import { importCollectionFromCardnexus } from "@/lib/cardnexus/import-collection";

type CnAccount = {
  id: string;
  email: string | null;
  username: string | null;
};

type Props = {
  compact?: boolean;
  returnTo?: string;
  autoImport?: boolean;
  onConnectionChange?: (connected: boolean, account: CnAccount | null) => void;
  onCollectionImported?: (
    info: Awaited<ReturnType<typeof importCollectionFromCardnexus>>,
  ) => void;
};

export default function CardNexusConnectPanel({
  compact,
  returnTo = "/inventaire",
  autoImport = true,
  onConnectionChange,
  onCollectionImported,
}: Props) {
  const [account, setAccount] = useState<CnAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const onConnectionChangeRef = useRef(onConnectionChange);
  onConnectionChangeRef.current = onConnectionChange;
  const onCollectionImportedRef = useRef(onCollectionImported);
  onCollectionImportedRef.current = onCollectionImported;
  const lastConnectedRef = useRef<boolean | null>(null);
  const importedOnceRef = useRef(false);

  const notify = useCallback((connected: boolean, next: CnAccount | null) => {
    if (lastConnectedRef.current === connected) return;
    lastConnectedRef.current = connected;
    onConnectionChangeRef.current?.(connected, next);
  }, []);

  const runImport = useCallback(async (refresh = false) => {
    setImporting(true);
    setError(null);
    try {
      const result = await importCollectionFromCardnexus({ refresh });
      onCollectionImportedRef.current?.(result);
      if (result.matched > 0) {
        setOkMsg(
          result.unmatched > 0
            ? `Collection importée · ${result.matched} cartes · ${result.unmatched} non reconnue(s)`
            : `Collection importée · ${result.matched} cartes depuis CardNexus`,
        );
      } else if (result.count > 0) {
        setOkMsg(`${result.count} ligne(s) trouvées mais aucune carte reconnue.`);
      } else {
        setOkMsg("CardNexus lié — inventaire Riftbound vide.");
      }
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import collection impossible");
      return null;
    } finally {
      setImporting(false);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const data = await cardnexusFetch<{ account: CnAccount }>("/api/cardnexus/me");
      setAccount(data.account);
      notify(true, data.account);
      if (autoImport && !importedOnceRef.current) {
        importedOnceRef.current = true;
        void runImport();
      }
    } catch {
      setAccount(null);
      notify(false, null);
    }
  }, [autoImport, notify, runImport]);

  useEffect(() => {
    // Handle return from OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get("cn") === "connected") {
      setOkMsg("CardNexus autorisé — import de ta collection…");
      importedOnceRef.current = false;
      params.delete("cn");
      const clean = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
      window.history.replaceState({}, "", clean);
    }
    const cnError = params.get("cn_error");
    if (cnError) {
      setError(decodeURIComponent(cnError));
      params.delete("cn_error");
      const clean = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
      window.history.replaceState({}, "", clean);
    }
    void refreshMe();
  }, [refreshMe]);

  async function onDisconnect() {
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      await fetch("/api/cardnexus/disconnect", { method: "DELETE", credentials: "same-origin" });
      setAccount(null);
      importedOnceRef.current = false;
      lastConnectedRef.current = null;
      notify(false, null);
      setOkMsg("CardNexus déconnecté.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Déconnexion impossible");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={
        compact
          ? "rounded-2xl border border-white/10 bg-white/[0.03] p-4"
          : "mt-8 rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-5"
      }
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-white">CardNexus</h2>
          <p className="mt-1 text-sm text-gray-400">
            Autorise RiftHub sur CardNexus pour importer automatiquement ta collection Riftbound.
          </p>
        </div>
        {account ? (
          <span className="rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-bold text-emerald-300">
            Lié · {account.username || account.email || account.id.slice(0, 8)}
          </span>
        ) : (
          <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-gray-400">
            Non lié
          </span>
        )}
      </div>

      {account ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            Collection synchronisée via l&apos;intégration CardNexus. Les quantités modifiées ici
            sont écrites chez CardNexus.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || importing}
              onClick={() => void runImport(true)}
              className="rounded-xl bg-cyan-600 px-4 py-2 text-sm font-bold hover:bg-cyan-500 disabled:opacity-50"
            >
              {importing ? "Import…" : "Réimporter la collection"}
            </button>
            <button
              type="button"
              disabled={busy || importing}
              onClick={() => void onDisconnect()}
              className="rounded-xl border border-white/15 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-white/5 disabled:opacity-50"
            >
              Déconnecter
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <a
            href={cardnexusConnectUrl(returnTo)}
            className="inline-flex rounded-xl bg-cyan-600 px-4 py-2 text-sm font-bold hover:bg-cyan-500"
          >
            Connecter CardNexus
          </a>
          <p className="text-xs text-gray-500">
            Tu seras redirigé vers CardNexus pour autoriser RiftHub, puis ta collection sera
            importée automatiquement.
          </p>
        </div>
      )}

      {okMsg && <p className="mt-3 text-sm text-emerald-400">{okMsg}</p>}
      {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
    </div>
  );
}
